/**
 * Generic Excel-sheet helpers used by ExcelSheetPopup and page-specific
 * configs (Finance HQ List, and any future page that wants the same grid).
 *
 * Keep page-specific column maps in sibling modules so this file stays
 * reusable: add a new config, pass it as `columns` to ExcelSheetPopup.
 */

export type ExcelSheetColumnType = "text" | "date" | "currency" | "number" | "percent";

export type ExcelSheetColumn = {
  id: string;
  label: string;
  type?: ExcelSheetColumnType;
  editable?: boolean;
  align?: "left" | "right" | "center";
  minWidth?: number;
  /** When true, the footer row sums this column. */
  total?: boolean;
  /**
   * When set, the cell is split into sub-rows from `row[subRowsFrom]`.
   * Each item is a record keyed by this column's `id`.
   */
  subRowsFrom?: string;
  /**
   * When set with `subRowsFrom`, this column splits each parent sub-row using
   * `subRow[nestedSubRowsFrom]`. Parent `subRowsFrom` columns span that height.
   */
  nestedSubRowsFrom?: string;
  /** Color the cell from another row/sub-row field using the invoice pay-status palette. */
  colorFrom?: string;
  rightClickTooltip?: string;
  feeContextField?: string;
  feeIcon?: "paid" | "edit";
  secondaryAction?: {
    tooltip: string;
    contextField: string;
    icon?: "paid" | "edit";
  };
  /** Label of the group this column belongs to, shown as a merged header above the column header row. Consecutive columns sharing the same group are merged into one cell. */
  group?: string;
  popupEdit?: boolean;
  popupEditIconWhenEmpty?: boolean;
  dueField?: string;
  /** Purely visual: shows a static pencil icon next to the value (when non-empty) with no click behavior of its own. */
  staticEditIcon?: boolean;
  showCurrencySymbol?: boolean;
  /** For fee-action columns (rightClickTooltip set): show the pay/edit icon(s) always, instead of only after an initial click, and make clicking an icon act immediately. */
  alwaysShowFeeIcon?: boolean;
};

/** Invoice pay-status colors used across Finance HQ / commission screens. */
export const INVOICE_PAY_STATUS_COLORS = {
  PAID: "#28a745",
  UNPAID: "#dc3545",
  PARTIAL: "#D97706",
} as const;

export type ExcelSheetRow = Record<string, unknown>;

export const getInvoicePayStatusColor = (status: unknown): string | undefined => {
  const key = String(status || "").trim().toUpperCase();
  if (key === "PAID") return INVOICE_PAY_STATUS_COLORS.PAID;
  if (key === "UNPAID") return INVOICE_PAY_STATUS_COLORS.UNPAID;
  if (key === "PARTIAL") return INVOICE_PAY_STATUS_COLORS.PARTIAL;
  return undefined;
};

export const getExcelStatusColor = (
  column: ExcelSheetColumn,
  row: ExcelSheetRow,
  subRow?: Record<string, unknown>,
  nestedRow?: Record<string, unknown>
): string | undefined => {
  if (!column.colorFrom) return undefined;
  const fromNested = nestedRow?.[column.colorFrom];
  const fromSub = subRow?.[column.colorFrom];
  const status =
    fromNested !== undefined && fromNested !== null && fromNested !== ""
      ? fromNested
      : fromSub !== undefined && fromSub !== null && fromSub !== ""
        ? fromSub
        : row[column.colorFrom];
  return getInvoicePayStatusColor(status);
};

/** 0 -> A, 25 -> Z, 26 -> AA */
export const toColLetters = (index: number): string => {
  let n = index + 1;
  let s = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    s = String.fromCharCode(65 + rem) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
};

export const parseExcelNumber = (value: unknown): number => {
  if (value === null || value === undefined || value === "") return 0;
  const num = Number(String(value).replace(/[$,]/g, "").trim());
  return Number.isNaN(num) ? 0 : num;
};

export const formatExcelCurrency = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "0.00";
  const num = parseExcelNumber(value);
  if (Number.isNaN(Number(String(value).replace(/[$,]/g, "").trim()))) {
    return String(value);
  }
  return num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const formatExcelPercent = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "";
  const raw = String(value).trim();
  if (raw.endsWith("%")) {
    const num = parseExcelNumber(raw.replace(/%/g, ""));
    return Number.isNaN(num) ? raw : `${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
  }
  const num = parseExcelNumber(value);
  if (Number.isNaN(Number(String(value).replace(/[$,%]/g, "").trim()))) {
    return raw;
  }
  return `${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
};

/**
 * TZ-stable date display. MM/DD/YYYY and YYYY-MM-DD are formatted from the
 * literal calendar day so CI boxes behind UTC cannot roll the date back.
 */
export const formatExcelDate = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "";
  const raw = String(value).trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) return raw;
  const isoDay = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDay) return `${isoDay[2]}/${isoDay[3]}/${isoDay[1]}`;
  return raw;
};

/** Empty is always valid (clearing a cell) — only a non-empty value must parse as a real number. */
export const isValidExcelNumber = (value: unknown): boolean => {
  if (value === null || value === undefined || String(value).trim() === "") return true;
  const raw = String(value).replace(/[$,]/g, "").trim();
  return /^-?\d+(\.\d+)?$/.test(raw);
};

/** Empty is always valid — a non-empty value must be MM/DD/YYYY or YYYY-MM-DD and a real calendar day. */
export const isValidExcelDate = (value: unknown): boolean => {
  if (value === null || value === undefined || String(value).trim() === "") return true;
  const raw = String(value).trim();
  const mdy = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  const iso = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  const parts = mdy
    ? { month: Number(mdy[1]), day: Number(mdy[2]), year: Number(mdy[3]) }
    : iso
      ? { month: Number(iso[2]), day: Number(iso[3]), year: Number(iso[1]) }
      : null;
  if (!parts) return false;
  const { month, day, year } = parts;
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
};

/** Strips keystrokes that can never be valid for the column's type, while the user is still typing. */
export const filterExcelInputByType = (type: ExcelSheetColumnType | undefined, raw: string): string => {
  if (type === "currency" || type === "number") {
    let out = raw.replace(/[^0-9.,\-]/g, "");
    const hasLeadingMinus = out.startsWith("-");
    out = out.replace(/-/g, "");
    if (hasLeadingMinus) out = `-${out}`;
    const firstDot = out.indexOf(".");
    if (firstDot !== -1) {
      out = out.slice(0, firstDot + 1) + out.slice(firstDot + 1).replace(/\./g, "");
    }
    return out;
  }
  if (type === "date") {
    return raw.replace(/[^0-9/\-]/g, "");
  }
  return raw;
};

export const toPatientInitials = (name: unknown): string => {
  if (name === null || name === undefined || name === "") return "";
  const parts = String(name)
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return `${parts[0].charAt(0).toUpperCase()}.`;
  const first = parts[0].charAt(0).toUpperCase();
  const last = parts[parts.length - 1].charAt(0).toUpperCase();
  return `${first}.${last}.`;
};

const firstNonEmpty = (...values: unknown[]): string => {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return "";
};

const joinNameParts = (...parts: unknown[]): string =>
  parts
    .map((part) => (part === null || part === undefined ? "" : String(part).trim()))
    .filter(Boolean)
    .join(" ");

export type PatientNameLookup = {
  patientId?: string | number;
  firstName?: string;
  middleName?: string;
  lastName?: string;
};

/** Resolve a visible patient name from the many shapes the invoice APIs return. */
export const resolvePatientDisplayName = (
  invoice: any,
  patients?: PatientNameLookup[]
): string => {
  const fromDirect = firstNonEmpty(
    invoice?.patientName,
    invoice?.patient,
    invoice?.pt,
    invoice?.patientFullName,
    invoice?.orderInfo?.patientName,
    invoice?.orderInfo?.patient,
    invoice?.order?.patientName,
    invoice?.order?.patient,
    invoice?.orderItems?.[0]?.patientName,
    invoice?.orderItems?.[0]?.patient
  );
  if (fromDirect) return fromDirect;

  const fromParts = joinNameParts(
    invoice?.patientFirstName || invoice?.firstName,
    invoice?.patientMiddleName || invoice?.middleName,
    invoice?.patientLastName || invoice?.lastName
  );
  if (fromParts) return fromParts;

  const patientId =
    invoice?.patientId ?? invoice?.order?.patientId ?? invoice?.orderInfo?.patientId;
  if (patientId != null && patients && patients.length > 0) {
    const match = patients.find((p) => String(p.patientId) === String(patientId));
    if (match) {
      return joinNameParts(match.firstName, match.middleName, match.lastName);
    }
  }
  return "";
};

const invoiceListRoot = (response: unknown): any =>
  (response as any)?.data?.data ?? (response as any)?.data ?? response ?? {};

export const extractInvoiceRecords = (response: unknown): any[] => {
  const root = invoiceListRoot(response);
  if (Array.isArray(root?.downloadData)) return root.downloadData;
  if (Array.isArray(root?.content)) return root.content;
  if (Array.isArray(root)) return root;
  return [];
};

export type InvoiceListPage = {
  records: any[];
  totalElements: number;
  summary?: Record<string, number>;
};

/** Prefer download dumps when present; otherwise use paginated `content`. */
export const extractInvoiceListPage = (response: unknown): InvoiceListPage => {
  const root = invoiceListRoot(response);
  const downloadData = Array.isArray(root?.downloadData) ? root.downloadData : null;
  const content = Array.isArray(root?.content) ? root.content : null;
  const records =
    downloadData && downloadData.length > 0
      ? downloadData
      : content
        ? content
        : extractInvoiceRecords(response);
  const totalRaw = Number(root?.totalElements);
  const totalElements =
    Number.isFinite(totalRaw) && totalRaw >= 0 ? totalRaw : records.length;
  const summarySource = root?.invoiceSummary || root?.summary;
  const summary =
    summarySource && typeof summarySource === "object" && !Array.isArray(summarySource)
      ? (summarySource as Record<string, number>)
      : undefined;
  return { records, totalElements, summary };
};

const escapeCsvCell = (value: unknown): string => {
  const text = value === null || value === undefined ? "" : String(value);
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

export const getExcelSubRows = (
  row: ExcelSheetRow,
  columns: ExcelSheetColumn[]
): Record<string, unknown>[] => {
  const source = columns.find((col) => col.subRowsFrom)?.subRowsFrom;
  if (!source) return [{}];
  const list = row[source];
  return Array.isArray(list) && list.length > 0 ? (list as Record<string, unknown>[]) : [{}];
};

export const getExcelNestedSource = (columns: ExcelSheetColumn[]): string | undefined =>
  columns.find((col) => col.nestedSubRowsFrom)?.nestedSubRowsFrom;

export const getExcelNestedSubRows = (
  subRow: Record<string, unknown> | undefined,
  nestedSource?: string
): Record<string, unknown>[] => {
  if (!nestedSource) return [{}];
  const list = subRow?.[nestedSource];
  return Array.isArray(list) && list.length > 0 ? (list as Record<string, unknown>[]) : [{}];
};

export const getExcelVisualLineCount = (
  row: ExcelSheetRow,
  columns: ExcelSheetColumn[]
): number => {
  const subRows = getExcelSubRows(row, columns);
  const nestedSource = getExcelNestedSource(columns);
  if (!nestedSource) return Math.max(1, subRows.length);
  return (
    subRows.reduce(
      (sum, subRow) => sum + Math.max(1, getExcelNestedSubRows(subRow, nestedSource).length),
      0
    ) || 1
  );
};

export const getExcelCellValue = (
  row: ExcelSheetRow,
  column: ExcelSheetColumn,
  subRow?: Record<string, unknown>,
  nestedRow?: Record<string, unknown>
): unknown => {
  if (column.nestedSubRowsFrom) return nestedRow?.[column.id];
  if (column.subRowsFrom) return subRow?.[column.id];
  return row[column.id];
};

export const buildExcelCsv = (
  columns: ExcelSheetColumn[],
  rows: ExcelSheetRow[],
  includeTotals = false
): string => {
  const header = columns.map((col) => escapeCsvCell(col.label)).join(",");
  const body: string[] = [];
  const nestedSource = getExcelNestedSource(columns);
  rows.forEach((row) => {
    const subRows = getExcelSubRows(row, columns);
    let visualIndex = 0;
    subRows.forEach((subRow) => {
      const nestedRows = nestedSource ? getExcelNestedSubRows(subRow, nestedSource) : [{}];
      nestedRows.forEach((nestedRow, nestedIndex) => {
        body.push(
          columns
            .map((col) =>
              escapeCsvCell(
                col.nestedSubRowsFrom
                  ? nestedRow[col.id]
                  : col.subRowsFrom
                    ? nestedIndex === 0
                      ? subRow[col.id]
                      : ""
                    : visualIndex === 0
                      ? row[col.id]
                      : ""
              )
            )
            .join(",")
        );
        visualIndex += 1;
      });
    });
  });
  if (includeTotals && rows.length > 0) {
    const totalRow = columns.map((col, idx) => {
      if (idx === 0) return escapeCsvCell("TOTAL");
      if (!col.total) return "";
      const value = sumExcelColumn(rows, col.id, columns);
      const prefix = col.type === "currency" && col.showCurrencySymbol ? "$" : "";
      const formatted =
        col.type === "currency"
          ? formatExcelCurrency(value)
          : value.toLocaleString("en-US", { maximumFractionDigits: 0 });
      return escapeCsvCell(`${prefix}${formatted}`);
    });
    body.push(totalRow.join(","));
  }
  return [header, ...body].join("\r\n");
};

export const downloadCsv = (csv: string, fileName: string): void => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

export const sumExcelColumn = (
  rows: ExcelSheetRow[],
  columnId: string,
  columns?: ExcelSheetColumn[]
): number => {
  const column = columns?.find((col) => col.id === columnId);
  if (column?.nestedSubRowsFrom) {
    return rows.reduce((sum, row) => {
      const subRows = getExcelSubRows(row, columns || []);
      return (
        sum +
        subRows.reduce(
          (subSum, subRow) =>
            subSum +
            getExcelNestedSubRows(subRow, column.nestedSubRowsFrom).reduce(
              (nestedSum, nestedRow) => nestedSum + parseExcelNumber(nestedRow[columnId]),
              0
            ),
          0
        )
      );
    }, 0);
  }
  if (column?.subRowsFrom) {
    return rows.reduce((sum, row) => {
      const subRows = getExcelSubRows(row, columns || []);
      return (
        sum +
        subRows.reduce((subSum, subRow) => subSum + parseExcelNumber(subRow[columnId]), 0)
      );
    }, 0);
  }
  return rows.reduce((sum, row) => sum + parseExcelNumber(row[columnId]), 0);
};

export const formatCellValue = (
  column: ExcelSheetColumn,
  value: unknown
): string => {
  if (column.type === "currency") {
    const formatted = formatExcelCurrency(value);
    const isNumericDisplay = /^-?[\d,]+\.\d{2}$/.test(formatted);
    return column.showCurrencySymbol && isNumericDisplay ? `$${formatted}` : formatted;
  }
  if (column.type === "percent") return formatExcelPercent(value);
  if (column.type === "date") return formatExcelDate(value);
  if (value === null || value === undefined) return "";
  return String(value);
};
